import { OutstandingAgreementType, ShineStartStatus } from '__generated__/GQL';
import getIsAgreementCompleted from 'common/bento/modules/Agreement/libs/getIsAgreementCompleted';
import { MachineGuards } from 'common/bento/types/MachineConfig';
import getCompanyComplianceModuleStatus, {
  CompanyComplianceModuleStatus,
} from 'features/Onboarding/modules/CompanyCompliance/libs/getCompanyComplianceModuleStatus';
import isPersonalInfoModuleCompleted from 'features/Onboarding/modules/PersonalInfo/libs/isPersonalInfoModuleCompleted';
import { ApplicantType } from 'features/Onboarding/types';

import { Model } from './model';

export enum Guard {
  HasCompletedUserAgreement = 'hasCompletedUserAgreement',
  IsApplicationSetupFinished = 'isApplicationSetupFinished',
  IsAwaitingAccountValidation = 'isAwaitingAccountValidation',
  IsShineStartPaid = 'isShineStartPaid',
  IsShineStartRefused = 'isShineStartRefused',
  ShouldAnswerBusinessInfo = 'shouldAnswerBusinessInfo',
  ShouldAnswerCompanyCompliance = 'shouldAnswerCompanyCompliance',
  ShouldAnswerPersonalInfo = 'shouldAnswerPersonalInfo',
}

const guards: MachineGuards<Model, Guard> = {
  hasCompletedUserAgreement: ({ getModuleData }) => {
    const { outstandingAgreements } = getModuleData().data.viewer;

    return getIsAgreementCompleted({
      agreementType: OutstandingAgreementType.Cgu,
      outstandingAgreements: outstandingAgreements || [],
    });
  },

  isApplicationSetupFinished: ({ getModuleData }) =>
    Boolean(
      getModuleData().data.viewer.companyUser?.hasFinishedApplicationSetup,
    ),

  isAwaitingAccountValidation: ({ getModuleData }) =>
    Boolean(
      getModuleData().data.viewer.companyUser?.isAwaitingAccountValidation,
    ),

  isShineStartPaid: ({ getModuleData }) =>
    Boolean(getModuleData().data.viewer.company?.shineStart?.isPaid),

  isShineStartRefused: ({ getModuleData }) =>
    Boolean(
      getModuleData().data.viewer.company?.shineStart?.status ===
        ShineStartStatus.Refused,
    ),

  shouldAnswerBusinessInfo: ({ getModuleData }) => {
    const { company } = getModuleData().data.viewer;

    if (!company || !company.shineStart) {
      return false;
    }

    return [ShineStartStatus.Pending, ShineStartStatus.Refused].includes(
      company.shineStart.status,
    );
  },

  shouldAnswerCompanyCompliance: ({ getModuleData }) => {
    const { shouldAskSanctionsDeclarationQuestions, viewer } =
      getModuleData().data;
    const { company } = viewer;

    if (!company) {
      return false;
    }

    const companyComplianceModuleStatus = getCompanyComplianceModuleStatus({
      companyProfile: company.profile,
      shouldAskSanctionsDeclarationQuestions,
    });

    return [
      CompanyComplianceModuleStatus.ToDo,
      CompanyComplianceModuleStatus.Refused,
    ].includes(companyComplianceModuleStatus);
  },

  shouldAnswerPersonalInfo: ({ getModuleData }) => {
    const { company, profile: userProfile } = getModuleData().data.viewer;

    // If there's no company, the user most likely just started the Shine Start flow
    if (!company) {
      return true;
    }

    return !isPersonalInfoModuleCompleted({
      applicantType: ApplicantType.AccountHolder,
      companyLegalBeneficiaries: [],
      companyProfile: company.profile,
      userProfile,
    });
  },
};

export default guards;
